import type { ReelItemProperty } from './types';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import type { ShortVideoProperty } from '~/core/entities/property';
import type { ComputedRef } from 'vue';

import {
	getFallbackBrokerImage,
	getFallbackCompanyImage
} from '@/utils/imageUrl';
import { getAreaLabel } from '~/core/entities/SystemEnums';

import { usePropertyInfo } from '~/components/VideoCard/usePropertyInfo';

/**
 * Public label for the property agent on reels: `users.display_name` when set,
 * otherwise the agent `name` field (API `name`).
 */
export function getReelsAgentDisplayLabel(
	agent: ReelItemProperty['agent'] | undefined,
	emptyFallback: string
): string {
	if (!agent) return emptyFallback;
	if ('display_name' in agent) {
		const fromProfile = (agent.display_name ?? '').trim();
		if (fromProfile) return fromProfile;
	}
	if ('displayName' in agent) {
		const fromProfile = (
			(agent as { displayName?: string | null }).displayName ?? ''
		).trim();
		if (fromProfile) return fromProfile;
	}
	const fromName = (agent.name ?? '').trim();
	return fromName || emptyFallback;
}

export function useShortsPropertyCardTexts(
	property: ComputedRef<ReelItemProperty | undefined>
) {
	const { ORIGIN } = useRuntimeConfig().public;

	const shortProperty = computed(
		() => property.value as ShortVideoProperty | undefined
	);

	const {
		formattedPrice,
		formattedArea,
		roomsInfo,
		region,
		propertyLocation,
		cityState
	} = usePropertyInfo(shortProperty);

	const agentName = computed(() =>
		getReelsAgentDisplayLabel(property.value?.agent, '')
	);
	const agentFirstName = computed(() =>
		getReelsAgentDisplayLabel(property.value?.agent, 'corretor')
	);
	const areaLabel = computed(() => {
		const propertyType = property.value?.propertyType;
		const slug =
			propertyType && 'slug' in propertyType ? propertyType.slug : undefined;
		return getAreaLabel(slug as PropertyTypeEnum, true);
	});
	const companyName = computed(() => property.value?.company?.name ?? '');

	const brokerSrc = computed(() => {
		const agent = property.value?.agent;
		if (!agent) return getFallbackBrokerImage(ORIGIN);
		const pic =
			'profilePictureUrl' in agent
				? agent.profilePictureUrl
				: 'profilePicture' in agent
					? (agent as { profilePicture?: { url?: string } }).profilePicture?.url
					: undefined;
		return pic || getFallbackBrokerImage(ORIGIN);
	});

	const companySrc = computed(() => {
		const company = property.value?.company;
		if (!company) return getFallbackCompanyImage(ORIGIN);
		const logo =
			'logoUrl' in company
				? company.logoUrl
				: 'smallLogo' in company
					? (company as { smallLogo?: string }).smallLogo
					: undefined;
		return logo || getFallbackCompanyImage(ORIGIN);
	});

	return {
		formattedPrice,
		formattedArea,
		areaLabel,
		roomsInfo,
		region,
		propertyLocation,
		cityState,
		agentName,
		agentFirstName,
		companyName,
		brokerSrc,
		companySrc
	};
}
